import { Identity, JwtData } from '../../models/auth';
import { AppDispatch, RootState, store } from '../index';
import { FetchState } from '../fetchState';
import { jwtDecode } from 'jwt-decode';
import { UserPreferences } from '@qcs/safety-client';
import i18n from 'i18next';
import { baseSlice, BaseType } from '../baseHelper';
import { hasOtherRoleThan, hasRole, isExternal } from '../../utils/roles';
import { getQcsAdminLastCompany, setAccessToken } from '../../utils/storage';
import { companyCustomizationActions } from './companyCustomization';
import { businessSettingsActions } from './bussinesSettings';
import { optionsEstablishmentActions } from './optionsEstablishment';
import { setActiveScope } from './dashboardTiles';
import { AUTOCOMPLETE_MAX_ITEMS, PAGINATION_DEFAULT, PAGINATION_OPTIONS } from '../../utils/constants';
import { SUPPORTED_LANGUAGES } from '../../utils/i18n';
import { firstUpper } from '../../utils/format';
import { invitationListActions } from './invitationList';
import { UserRole } from '@api/models/enums/UserRole';
import { InvitationState } from '@api/models/enums/InvitationState';
import { EntityState } from '@api/models/enums/EntityState';
import { companySettingsService, establishmentService, userService } from '@api/services/services';
import { UserState } from '@api/models/enums/UserState';

export type IdentityState = BaseType<Identity>;

const initialState: IdentityState = {
  state: FetchState.None,
  data: {
    id: '',
    email: '',
    name: '',
    roles: [],
    companyId: '',
    rowsCount: PAGINATION_DEFAULT,
    welcomeLangPages: {},
    supplier: '',
  },
};

export const identitySlice = baseSlice({
  name: 'identity',
  initialState,
});

export const selectIdentityState = (state: RootState) => state.identity.state;
export const selectIdentity = (state: RootState) => state.identity.data;
export const selectIdentityRoles = (state: RootState) => state.identity.data.roles;
export const selectIdentityCompanyId = (state: RootState) => state.identity.data.companyId;
export const selectIdentityRowsCount = (state: RootState) => state.identity.data.rowsCount;
export const identityActions = identitySlice.actions;

export const getIdentity = (token: string, loggedNow: boolean) => async (dispatch: AppDispatch) => {
  const identityState = selectIdentityState(store.getState());
  if (identityState !== FetchState.None) {
    return;
  }

  dispatch(identityActions.request());
  try {
    if (loggedNow) {
      setAccessToken(token);
    }

    const data = jwtDecode<JwtData>(token);

    const roles = data.scope.split(' ') as UserRole[];

    let preferences: UserPreferences | undefined = undefined;
    if (hasOtherRoleThan(roles, [UserRole.RemoteToken])) {
      const preferencesRes = await userService.getPreferences(data.sub);
      preferences = preferencesRes.data;
    }

    const identity: Identity = {
      id: data.sub,
      email: data.email,
      name: data.name ? data.name : data.email,
      roles,
      companyId: data.company,
      rowsCount: PAGINATION_OPTIONS.includes(preferences?.pageSize ?? 0) ? preferences!.pageSize : PAGINATION_DEFAULT,
      welcomeLangPages: {},
      supplier: data.supplier,
      preferences,
    };

    if (loggedNow) {
      const isExternalManager = hasRole(identity.roles, [UserRole.ExternalManager]);
      const isExternalWorker = hasRole(identity.roles, [UserRole.ExternalWorker]);
      const isExternalOneTime = hasRole(identity.roles, [UserRole.ExternalOneTime]);

      if (isExternalManager || isExternalWorker || isExternalOneTime) {
        for (const lang of SUPPORTED_LANGUAGES) {
          const settings = (preferences as any)['settings' + firstUpper(lang.code)];

          if (isExternalManager) {
            identity.welcomeLangPages[lang.code] = !settings?.supplierEmployeeSeen;
          } else if (isExternalWorker) {
            identity.welcomeLangPages[lang.code] = !settings?.supplierEmployeeWorkerSeen;
          } else {
            identity.welcomeLangPages[lang.code] = !settings?.visitationSeen;
          }
        }
      }
    }

    i18n.changeLanguage(preferences?.language ?? data.lang);

    if (hasRole(identity.roles, [UserRole.AdminQcs])) {
      const lastCompanyId = getQcsAdminLastCompany();
      if (lastCompanyId) {
        identity.companyId = lastCompanyId;
      }
      dispatch(setActiveScope('company'));
    }

    dispatch(setDefaultFilter(identity));

    if (identity.companyId) {
      dispatch(companyCustomizationActions.request());
      dispatch(businessSettingsActions.request());
      dispatch(optionsEstablishmentActions.request());

      const canQueryEstablishments = hasRole(identity.roles, [
        UserRole.ManagerWorkplace,
        UserRole.ManagerOsah,
        UserRole.AdminCompany,
        UserRole.Receptionist,
      ]);

      const [companySettingsResponse, establishmentsResponse] = await Promise.all([
        companySettingsService.getBusinessSettings({ companyId: identity.companyId }),
        canQueryEstablishments
          ? establishmentService.query({
              companyId: identity.companyId,
              state: EntityState.Active,
              nameOnly: true,
              page: 0,
              size: AUTOCOMPLETE_MAX_ITEMS,
              sort: ['name', 'asc'],
            })
          : Promise.resolve({ data: { content: [] } }),
      ]);

      dispatch(companyCustomizationActions.success(companySettingsResponse.data));
      dispatch(businessSettingsActions.success(companySettingsResponse.data));
      dispatch(optionsEstablishmentActions.success(establishmentsResponse.data.content ?? []));
    } else {
      dispatch(companyCustomizationActions.success(undefined));
    }

    dispatch(identityActions.success(identity));
  } catch (e) {
    dispatch(identityActions.error());
  }
};

export const setDefaultFilter = (identity: Identity) => (dispatch: AppDispatch) => {
  const external = isExternal(identity);
  const isExternalManager = hasRole(identity.roles, [UserRole.ExternalManager]);

  //Default invitation filter.
  dispatch(
    invitationListActions.updateFilter({
      employee: external ? undefined : { id: identity.id, name: identity.name },
      supplierEmployee: isExternalManager
        ? {
            id: identity.id,
            name: identity.name,
            email: '',
            language: '',
            lastName: '',
            role: UserRole.ExternalManager,
            state: UserState.Active,
          }
        : undefined,
      state: Object.values(InvitationState).filter((x) => x !== InvitationState.Archived),
    }),
  );
};
